// src/features/auth/api-layer/clients/AuthAPIClient.ts

import { BaseAPIClient } from './BaseAPIClient';
import { ENDPOINTS } from './endpoints';
import {
  LoginCredentials,
  RegistrationData,
  AuthResponse,
  TokenPair,
  User,
  PasswordResetRequest,
  PasswordResetConfirm,
  MFAVerifyRequest,
  MFASetupResponse,
  InviteValidationResponse,
  WorkspaceValidationResponse,
  RefreshTokenResponse,
  EmailVerificationRequest,
  SocialAuthRequest,
  DeviceTrustRequest,
  AuthProvider,
} from '../../types/auth.types';

export class AuthAPIClient extends BaseAPIClient {
  private static instance: AuthAPIClient;

  private constructor() {
    super();
  }

  // Singleton pattern for consistent auth client usage
  public static getInstance(): AuthAPIClient {
    if (!AuthAPIClient.instance) {
      AuthAPIClient.instance = new AuthAPIClient();
    }
    return AuthAPIClient.instance;
  }

  // Authentication Methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.LOGIN,
      credentials,
      { skipAuth: true }
    );
    return response.data;
  }

  async logout(): Promise<void> {
    await this.post(ENDPOINTS.AUTH.LOGOUT);
    this.clearAuthToken();
  }

  async register(data: RegistrationData): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.REGISTER,
      data,
      { skipAuth: true }
    );
    return response.data;
  }

  // The refresh token is an httpOnly cookie the backend sets at login and
  // rotates on each refresh. The browser sends it automatically (the client is
  // created with withCredentials), so we POST an empty body and read only the
  // new access token back — the rotated refresh token returns as a Set-Cookie
  // that JS never sees. The optional arg is ignored (kept for call-site
  // compatibility) so the refresh token is never handled in JS.
  async refreshToken(_refreshToken?: string): Promise<TokenPair> {
    const response = await this.post<RefreshTokenResponse>(
      ENDPOINTS.AUTH.REFRESH,
      {},
      { skipAuth: true }
    );

    // Update auth token for subsequent requests
    if (response.data.accessToken) {
      this.setAuthToken(response.data.accessToken);
    }

    return {
      accessToken: response.data.accessToken,
      refreshToken: '', // cookie-managed; never exposed to JS
      expiresIn: response.data.expiresIn,
      tokenType: response.data.tokenType || 'Bearer',
    };
  }

  // User Methods
  async getCurrentUser(): Promise<User> {
    const response = await this.get<User>(ENDPOINTS.AUTH.ME);
    return response.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.patch<User>(ENDPOINTS.AUTH.PROFILE, data);
    return response.data;
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.post(ENDPOINTS.AUTH.UPDATE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  }

  async updateUsername(newUsername: string): Promise<User> {
    const response = await this.post<User>(ENDPOINTS.AUTH.UPDATE_USERNAME, { newUsername });
    return response.data;
  } 

  async updateBio(newBio: string): Promise<User> {
    const response = await this.post<User>(ENDPOINTS.AUTH.UPDATE_BIO, { newBio });
    return response.data;
  }


  async updateProfilePicture(file: File): Promise<User> {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response = await this.post<User>(ENDPOINTS.AUTH.UPDATE_PROFILE_PICTURE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateEmail(newEmail: string, password: string): Promise<void> {
    await this.post(ENDPOINTS.AUTH.UPDATE_EMAIL, { newEmail, password });
  }

  // Password Methods
  async requestPasswordReset(data: PasswordResetRequest): Promise<void> {
    await this.post(ENDPOINTS.AUTH.PASSWORD_RESET_REQUEST, data, { skipAuth: true });
  }

  async confirmPasswordReset(data: PasswordResetConfirm): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.PASSWORD_RESET_CONFIRM,
      data,
      { skipAuth: true }
    );
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  }

  async validatePasswordResetToken(token: string): Promise<boolean> {
    try {
      await this.post('/auth/password/validate-token', { token }, { skipAuth: true });
      return true;
    } catch {
      return false;
    }
  }

  // Email Verification
  async verifyEmail(data: EmailVerificationRequest): Promise<void> {
    await this.post(ENDPOINTS.AUTH.VERIFY_EMAIL, data, { skipAuth: true });
  }

  async resendVerificationEmail(email: object): Promise<void> {
    await this.post(ENDPOINTS.AUTH.RESEND_VERIFICATION, { email }, { skipAuth: true });
  }

  // MFA Methods
  async setupMFA(): Promise<MFASetupResponse> {
    const response = await this.post<MFASetupResponse>(ENDPOINTS.AUTH.MFA_SETUP);
    return response.data;
  }

  async verifyMFA(data: MFAVerifyRequest): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.MFA_VERIFY,
      data
    );
    return response.data;
  }

  async disableMFA(password: string): Promise<void> {
    await this.post(ENDPOINTS.AUTH.MFA_DISABLE, { password });
  }

  async generateBackupCodes(): Promise<{ codes: string[] }> {
    const response = await this.post<{ codes: string[] }>(
      ENDPOINTS.AUTH.MFA_BACKUP_CODES
    );
    return response.data;
  }

  // Social Auth
  async socialLogin(data: SocialAuthRequest): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.SOCIAL_LOGIN,
      data,
      { skipAuth: true }
    );
    return response.data;
  }

  async linkSocialAccount(provider: string, token: string): Promise<void> {
    await this.post(ENDPOINTS.AUTH.SOCIAL_LINK, { provider, token });
  }

  async unlinkSocialAccount(provider: string): Promise<void> {
    await this.delete(`${ENDPOINTS.AUTH.SOCIAL_UNLINK}/${provider}`);
  }

  // Workspace/Tenant Methods
  async validateWorkspace(identifier: string): Promise<WorkspaceValidationResponse> {
    const response = await this.get<WorkspaceValidationResponse>(
      `/auth/workspace/${identifier}/validate`,
      { skipAuth: true }
    );
    return response.data;
  }

  // Get available auth providers for a workspace
  async getAuthProviders(workspace: string): Promise<{ providers: AuthProvider[] }> {
    const response = await this.get<{ providers: AuthProvider[] }>(
      `/auth/providers/${workspace}`,
      { skipAuth: true }
    );
    return response.data;
  }

  async switchWorkspace(workspaceId: string): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.SWITCH_WORKSPACE,
      { workspaceId }
    );
    return response.data;
  }

  // Invitation Methods
  async validateInvite(token: string): Promise<InviteValidationResponse> {
    const response = await this.get<InviteValidationResponse>(
      `${ENDPOINTS.AUTH.VALIDATE_INVITE}/${token}`,
      { skipAuth: true }
    );
    return response.data;
  }

  async acceptInvite(token: string, userData: Partial<User>): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>(
      ENDPOINTS.AUTH.ACCEPT_INVITE,
      { token, ...userData },
      { skipAuth: true }
    );
    return response.data;
  }

  // Device Trust
  async trustDevice(data: DeviceTrustRequest): Promise<void> {
    await this.post(ENDPOINTS.AUTH.TRUST_DEVICE, data);
  }

  async getTrustedDevices(): Promise<Array<{ id: string; name: string; lastUsed: string }>> {
    const response = await this.get<Array<{ id: string; name: string; lastUsed: string }>>(
      ENDPOINTS.AUTH.TRUSTED_DEVICES
    );
    return response.data;
  }

  async revokeTrustedDevice(deviceId: string): Promise<void> {
    await this.delete(`${ENDPOINTS.AUTH.TRUSTED_DEVICES}/${deviceId}`);
  }

  // Session Management
  async getActiveSessions(): Promise<Array<{ id: string; device: string; location: string; lastActive: string }>> {
    const response = await this.get<Array<{ id: string; device: string; location: string; lastActive: string }>>(
      ENDPOINTS.AUTH.SESSIONS
    );
    return response.data;
  }

  async revokeSession(sessionId: string): Promise<void> {
    await this.delete(`${ENDPOINTS.AUTH.SESSIONS}/${sessionId}`);
  }

  async revokeAllSessions(): Promise<void> {
    await this.post(ENDPOINTS.AUTH.REVOKE_ALL_SESSIONS);
  }

  // Utility Methods
  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    const response = await this.post<{ available: boolean }>(
      ENDPOINTS.AUTH.CHECK_EMAIL,
      { email },
      { skipAuth: true }
    );
    return response.data;
  }

  async validateCompanyCode(code: string): Promise<{ valid: boolean; companyName?: string }> {
    const response = await this.get<{ valid: boolean; companyName?: string }>(
      `${ENDPOINTS.AUTH.VALIDATE_COMPANY_CODE}/${code}`,
      { skipAuth: true }
    );
    return response.data;
  }

  // Tenant Methods
  async inviteUserToTenant(data: { email: string; role: string; tenantId?: string }): Promise<TenantInviteResponse> {
    const response = await this.post<TenantInviteResponse>('/tenant/users/invite', data);
    return response.data;
  }

  async updateTenant(tenantId: string, updates: Partial<TenantSettings>): Promise<TenantSettings> {
    const response = await this.patch<TenantSettings>(`/tenant/${tenantId}`, updates);
    return response.data;
  }

  async fetchTenantMembers(tenantId: string): Promise<TenantMember[]> {
    const response = await this.get<TenantMember[]>(`/tenant/${tenantId}/members`);
    return response.data;
  }
}

// Export singleton instance
export const authAPI = AuthAPIClient.getInstance();

// Tenant settings interface
export interface TenantSettings {
  name: string;
  domain: string;
  logo?: string;
  theme?: Record<string, unknown>;
  features?: string[];
  settings?: Record<string, unknown>;
}

// Tenant member interface
export interface TenantMember {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  joinedAt: string;
}

// Tenant invite response interface
export interface TenantInviteResponse {
  inviteId: string;
  email: string;
  role: string;
  tenantId: string;
  expiresAt: string;
}

// Tenant API instance
export const tenantAPI = {
  inviteUser: (data: { email: string; role: string; tenantId?: string }) => 
    authAPI.inviteUserToTenant(data),
  updateTenant: (tenantId: string, updates: Partial<TenantSettings>) => 
    authAPI.updateTenant(tenantId, updates),
  fetchMembers: (tenantId: string) => 
    authAPI.fetchTenantMembers(tenantId),
};